import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Radio,
  CircularProgress,
} from '@mui/material';
import { colors } from '../styles';
import { getCategoryAssignSources, assignCategoryMoney } from '../services/api';
import {
  formatAssignMoney,
  previewCategoryAssign,
} from '../utils/categoryAssign';

const CategoryAssignDialog = ({
  open,
  onClose,
  budgetId,
  categoryId,
  categoryName,
  onAssigned,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sources, setSources] = useState([]);
  const [targetLeft, setTargetLeft] = useState(0);
  const [totalBudget, setTotalBudget] = useState(0);
  const [amountText, setAmountText] = useState('');
  const [selectedSourceId, setSelectedSourceId] = useState(null);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (!open || !budgetId || !categoryId) return undefined;
    let cancelled = false;
    setAmountText('');
    setServerError(null);
    setLoading(true);
    getCategoryAssignSources(budgetId, categoryId)
      .then((data) => {
        if (cancelled) return;
        const nextSources = data?.sources || [];
        setSources(nextSources);
        setTargetLeft(data?.target?.left || 0);
        setTotalBudget(data?.totalBudget || 0);
        setSelectedSourceId(nextSources[0]?.id ?? null);
        if (data?.canAssign === false && data?.reason) {
          setServerError(data.reason);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setServerError(error?.response?.data?.error || 'Could not load categories to assign from.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, budgetId, categoryId]);

  const preview = previewCategoryAssign({
    amountText,
    sources,
    selectedSourceId,
    targetName: categoryName || 'this category',
    targetLeft,
    totalBudget,
  });
  const fieldError = preview.fieldError || serverError;
  const canSubmit = preview.canSubmit && !submitting && !loading && !serverError;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedSourceId) return;
    const amount = Number(String(amountText).replace(/[$,\s]/g, ''));
    setSubmitting(true);
    setServerError(null);
    try {
      const result = await assignCategoryMoney(budgetId, categoryId, amount, selectedSourceId);
      if (typeof onAssigned === 'function') onAssigned(result);
      onClose();
    } catch (error) {
      const message = error?.response?.data?.error;
      setServerError(typeof message === 'string' ? message : 'Could not assign that amount.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{`Assign money to ${categoryName || 'this category'}`}</DialogTitle>
      <DialogContent>
        <TextField
          label="Amount"
          value={amountText}
          onChange={(event) => setAmountText(event.target.value)}
          placeholder="$0.00"
          fullWidth
          autoFocus
          error={Boolean(fieldError)}
          helperText={fieldError || ' '}
          inputProps={{ 'aria-label': 'Amount', inputMode: 'decimal' }}
          sx={{
            mt: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '16px',
              backgroundColor: '#F4F5F8',
            },
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
          From
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          sources.map((source) => {
            const selected = source.id === selectedSourceId;
            const short = preview.shortSourceIds.includes(source.id);
            return (
              <Box
                key={source.id}
                onClick={() => setSelectedSourceId(source.id)}
                role="radio"
                aria-checked={selected}
                aria-label={`${source.name}, ${formatAssignMoney(source.left)} left`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1.25,
                  cursor: 'pointer',
                  borderBottom: `1px solid ${colors.primary}10`,
                }}
              >
                <Typography sx={{ flex: 1, fontWeight: 600 }}>{source.name}</Typography>
                <Typography
                  variant="body2"
                  sx={{ color: short ? colors.status.error : 'text.secondary' }}
                >
                  {`${formatAssignMoney(source.left)} left`}
                </Typography>
                <Radio checked={selected} sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />
              </Box>
            );
          })
        )}
        {preview.summary ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {preview.summary}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          sx={{
            bgcolor: colors.primary,
            borderRadius: 999,
            height: 54,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { bgcolor: colors.primary },
          }}
        >
          {submitting ? 'Assigning…' : preview.ctaLabel}
        </Button>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryAssignDialog;
